import { useAtom } from '@reatom/npm-react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { useMemo } from 'use-memo-one'

import { SearchInput } from '@blade/ui-kit'
import { WalletModelShort } from '@root/entities/wallet/ui'
import { Dialog, FadeOverflow, InfoMessage } from '@root/shared/ui-kit'

import { reatomManageHiddenAssets } from '../../atoms/reatom-manage-hidden-assets'
import { WalletAssetToggleItem } from '../common/wallet-asset-toggle-item'

import type { WalletModel } from '@root/entities/wallet/atoms'
import type { ClassNameProps } from '@root/shared/react'
import type { DialogProps } from '@root/shared/ui-kit'

type Props = {
  wallet: WalletModel
} & Pick<DialogProps, 'onOpenChange' | 'open'> &
  ClassNameProps

export const ManageHiddenAssetsDialog: React.FC<Props> = ({
  open,
  wallet,
  onOpenChange,
  className,
}) => {
  const { t } = useTranslation()

  const model = useMemo(
    // eslint-disable-next-line @reatom/unit-naming-rule
    () => reatomManageHiddenAssets({ wallet }),
    [wallet],
  )
  const [assets] = useAtom(model.assets)
  const [searchQuery, setSearchQuery] = useAtom(model.searchQuery)

  const renderResults = () => {
    if (assets.length === 0) {
      return <InfoMessage intent="empty" />
    }

    return (
      <List mode="always" variant="vertical">
        {assets.map((it) => (
          <WalletAssetToggleItem
            key={it.currencyAddress}
            currency={it.currency}
            wallet={wallet}
          />
        ))}
      </List>
    )
  }

  return (
    <Root
      variant="bordered"
      className={className}
      open={open}
      onOpenChange={onOpenChange}
      describedBy="assets-management-dialog-description"
    >
      <Dialog.Header
        title={
          <Header id="assets-management-dialog-description">
            {t('wallet.manage-assets')}
            <HeaderWalletShort wallet={wallet} size="xs" />
          </Header>
        }
      />
      <Content>
        <Top>
          {/* TODO: Ivan - check sizes xs vs h32 */}
          <Input
            size="h32"
            placeholder={t('currency.search-token')}
            value={searchQuery}
            onChange={(event) => setSearchQuery(event.target.value)}
          />
        </Top>
        {renderResults()}
      </Content>
    </Root>
  )
}

const Root = styled(Dialog)`
  width: 330px;
  height: 60vh;
  display: grid;
  grid-template-rows: auto minmax(0, 1fr);
  overflow-y: hidden;
`

const Header = styled.header`
  display: grid;
  grid-template-columns: auto auto;

  gap: 4px;
  justify-items: start;
  justify-content: start;
`

const HeaderWalletShort = styled(WalletModelShort)`
  margin-top: -1px;
`

const Top = styled.div`
  padding-inline: 12px;
  display: grid;
`

const List = styled(FadeOverflow)`
  overflow-y: auto;
`

const Content = styled.div`
  display: grid;
  grid-template-rows: auto minmax(0, 1fr);
  gap: 10px;
`

const Input = styled(SearchInput)`
  margin-top: 16px;
  height: 32px;
  padding: 0 8px;
  border-radius: 8px;
  font-size: 12px;
  gap: 4px;
`
